import React, { useMemo, useState } from "react";

import DataGrid, {
  Column,
  Editing,
  FilterRow,
  Form,
  Pager,
  Paging,
  Scrolling,
} from "devextreme-react/data-grid";
import { RequiredRule, SimpleItem } from "devextreme-react/form";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import "devextreme/ui/select_box";
import "devextreme/ui/text_area";

import { useWindowDimensions } from "@app/contexts";
import { safeApi } from "@app/statefx/helpers/fxhelpers";
import { xouterApi } from "@app/statefx/xapis";
import { store } from "@app/statefx/xstore";

import "./super.scss";

export type TWhatsNewEntry = {
  id?: string;
  major?: number;
  minor?: number;
  month?: number;
  day?: number;
  versionCode: string;
  description: string;
  statusCode: "DRAFT" | "RELEASED";
  sourceCode: "MANUAL" | "IMPORTED_MARKDOWN" | "AI_SVN";
  createdAt?: string;
  updatedAt?: string;
  releasedAt?: string | null;
};

export type TWhatsNewGeneration = {
  version: string;
  window?: {
    startExclusive: string;
    endInclusive: string;
    startSource: "PREVIOUS_RELEASE" | "EXPLICIT";
    previousVersion?: string | null;
  };
  draft: string | null;
  preserveExistingText: boolean;
  candidates: Array<{
    id: string;
    summary: string;
    occurredAt: string;
    sources: string[];
    productAreas: string[];
    evidence: Array<{
      source: string;
      repository?: string;
      reference: string;
      paths?: string[];
      labels?: string[];
      url?: string;
    }>;
  }>;
  coverage: Array<{
    source: string;
    status: string;
    elapsedMs: number;
    rawCount: number;
    candidateCount: number;
    truncated: boolean;
    error?: { code: string; message: string };
  }>;
  warnings: string[];
  truncation: { rawSvn: boolean; candidates: boolean; aiPayload: boolean };
};

type TRoadmapItem = {
  id?: string;
  title: string;
  description: string;
  statusCode: "PLANNED" | "COMPLETED" | "ABANDONED";
  horizonCode: "NEAR_TERM" | "LONG_TERM";
  targetVersion?: string | null;
  outcomeNote?: string;
  createdAt?: string;
  updatedAt?: string;
  closedAt?: string | null;
};

export const listWhatsNewRequest = xouterApi.get(
  "xapi/product-evolution/list-whatsnew",
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: "/swsbuilder/v2/moa/whatsnew",
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

export const readWhatsNewRequest = xouterApi.get<{ versionCode: string }>(
  "xapi/product-evolution/read-whatsnew",
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: `/swsbuilder/v2/moa/whatsnew/${encodeURIComponent(
        ctx.payload.versionCode
      )}`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

export const saveWhatsNewRequest = xouterApi.post<TWhatsNewEntry>(
  "xapi/product-evolution/save-whatsnew",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: "/swsbuilder/v2/moa/whatsnew",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ctx.payload),
    });
    yield* next();
    return ctx.json;
  }
);

export const generateWhatsNewRequest = xouterApi.post<{
  versionCode: string;
  existingText: string;
  startAt?: string;
}>("xapi/product-evolution/generate-whatsnew", function* (ctx, next) {
  ctx.request = ctx.req({
    method: "POST",
    url: `/swsbuilder/v2/moa/whatsnew/${encodeURIComponent(
      ctx.payload.versionCode
    )}/generate`,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      existingText: ctx.payload.existingText,
      ...(ctx.payload.startAt ? { startAt: ctx.payload.startAt } : {}),
    }),
  });
  yield* next();
  return ctx.json;
});

const deleteWhatsNewRequest = xouterApi.delete<{ id: string }>(
  "xapi/product-evolution/delete-whatsnew",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "DELETE",
      url: `/swsbuilder/v2/moa/whatsnew/${encodeURIComponent(ctx.payload.id)}`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

const listRoadmapRequest = xouterApi.get(
  "xapi/product-evolution/list-roadmap",
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: "/swsbuilder/v2/moa/roadmap",
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

const saveRoadmapRequest = xouterApi.post<TRoadmapItem>(
  "xapi/product-evolution/save-roadmap",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: "/swsbuilder/v2/moa/roadmap",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ctx.payload),
    });
    yield* next();
    return ctx.json;
  }
);

const deleteRoadmapRequest = xouterApi.delete<{ id: string }>(
  "xapi/product-evolution/delete-roadmap",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "DELETE",
      url: `/swsbuilder/v2/moa/roadmap/${encodeURIComponent(ctx.payload.id)}`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

export async function runProductEvolutionApi<T, P = undefined>(
  endpoint: any,
  payload?: P,
  signal?: AbortSignal
): Promise<T> {
  const task = store
    .getScope()
    .run(() => safeApi<T, P>(endpoint, payload));
  const halt = () => {
    void task.halt().catch(() => undefined);
  };
  if (signal?.aborted) halt();
  else signal?.addEventListener("abort", halt, { once: true });
  let result;
  try {
    result = await task;
  } finally {
    signal?.removeEventListener("abort", halt);
  }
  if (!result?.ok) {
    const error =
      result && "error" in result
        ? result.error
        : new Error("Product evolution request failed");
    throw error instanceof Error
      ? error
      : new Error(String(error));
  }
  return result.value;
}

const whatsNewStatuses = [
  { value: "DRAFT", text: "Ciornă" },
  { value: "RELEASED", text: "Lansat" },
];
const whatsNewSources = [
  { value: "MANUAL", text: "Manual" },
  { value: "IMPORTED_MARKDOWN", text: "Importat din whatsnew.md" },
  { value: "AI_SVN", text: "Completat de AI din SVN" },
];
const roadmapStatuses = [
  { value: "PLANNED", text: "Planificat" },
  { value: "COMPLETED", text: "Finalizat" },
  { value: "ABANDONED", text: "Abandonat" },
];
const roadmapHorizons = [
  { value: "NEAR_TERM", text: "Viitor apropiat" },
  { value: "LONG_TERM", text: "Viitor mai îndepărtat" },
];

export const WhatsNew = () => {
  const [activeTab, setActiveTab] = useState<"whatsnew" | "roadmap">(
    "whatsnew"
  );
  const { drawerHeight } = useWindowDimensions();

  const whatsNewDataSource = useMemo(
    () => {
      const rowsById = new Map<string, TWhatsNewEntry>();
      return new DataSource({
        store: new CustomStore({
          key: "id",
          loadMode: "raw",
          load: async () => {
            const rows = await runProductEvolutionApi<TWhatsNewEntry[]>(
              listWhatsNewRequest
            );
            rowsById.clear();
            rows.forEach((row) => row.id && rowsById.set(row.id, row));
            return rows;
          },
          insert: async (values) => {
            const saved = await runProductEvolutionApi<
              TWhatsNewEntry,
              TWhatsNewEntry
            >(
              saveWhatsNewRequest,
              values as TWhatsNewEntry
            );
            if (saved.id) rowsById.set(saved.id, saved);
            return saved;
          },
          update: async (key, values) => {
            const saved = await runProductEvolutionApi<
              TWhatsNewEntry,
              TWhatsNewEntry
            >(
              saveWhatsNewRequest,
              {
                ...(rowsById.get(String(key)) || {}),
                ...(values as TWhatsNewEntry),
                id: String(key),
              }
            );
            if (saved.id) rowsById.set(saved.id, saved);
            return saved;
          },
          remove: async (key) => {
            await runProductEvolutionApi<
              { id: string },
              { id: string }
            >(
              deleteWhatsNewRequest,
              { id: String(key) }
            );
            rowsById.delete(String(key));
          },
        }),
      });
    },
    []
  );

  const roadmapDataSource = useMemo(
    () => {
      const rowsById = new Map<string, TRoadmapItem>();
      return new DataSource({
        store: new CustomStore({
          key: "id",
          loadMode: "raw",
          load: async () => {
            const rows = await runProductEvolutionApi<TRoadmapItem[]>(
              listRoadmapRequest
            );
            rowsById.clear();
            rows.forEach((row) => row.id && rowsById.set(row.id, row));
            return rows;
          },
          insert: async (values) => {
            const saved = await runProductEvolutionApi<
              TRoadmapItem,
              TRoadmapItem
            >(
              saveRoadmapRequest,
              values as TRoadmapItem
            );
            if (saved.id) rowsById.set(saved.id, saved);
            return saved;
          },
          update: async (key, values) => {
            const saved = await runProductEvolutionApi<
              TRoadmapItem,
              TRoadmapItem
            >(
              saveRoadmapRequest,
              {
                ...(rowsById.get(String(key)) || {}),
                ...(values as TRoadmapItem),
                id: String(key),
              }
            );
            if (saved.id) rowsById.set(saved.id, saved);
            return saved;
          },
          remove: async (key) => {
            await runProductEvolutionApi<
              { id: string },
              { id: string }
            >(
              deleteRoadmapRequest,
              { id: String(key) }
            );
            rowsById.delete(String(key));
          },
        }),
      });
    },
    []
  );

  return (
    <div style={{ padding: "20px" }}>
      <div className="builder-header">
        <div>
          <h1>Whatsnew și roadmap</h1>
          <div className="subtitle-premium">
            Istoricul versiunilor și direcțiile viitoare Jurnal Gold
          </div>
        </div>
      </div>

      <div className="builder-tabs" style={{ marginBottom: "16px" }}>
        <button
          className={`tab-btn-simple ${activeTab === "whatsnew" ? "active" : ""}`}
          onClick={() => setActiveTab("whatsnew")}
        >
          WHATSNEW
        </button>
        <button
          className={`tab-btn-simple ${activeTab === "roadmap" ? "active" : ""}`}
          onClick={() => setActiveTab("roadmap")}
        >
          ROADMAP
        </button>
      </div>

      {activeTab === "whatsnew" ? (
        <DataGrid
          dataSource={whatsNewDataSource}
          height={Math.max(420, drawerHeight - 170)}
          showBorders={true}
          showRowLines={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          wordWrapEnabled={true}
          onInitNewRow={(event) => {
            event.data.statusCode = "DRAFT";
            event.data.sourceCode = "MANUAL";
          }}
        >
          <Editing
            mode="form"
            allowAdding={true}
            allowUpdating={true}
            allowDeleting={true}
            useIcons={true}
          >
            <Form labelMode="floating" labelLocation="top" colCount={2}>
              <SimpleItem dataField="versionCode">
                <RequiredRule message="Versiunea este obligatorie" />
              </SimpleItem>
              <SimpleItem
                dataField="sourceCode"
                editorType="dxSelectBox"
                editorOptions={{
                  items: whatsNewSources,
                  valueExpr: "value",
                  displayExpr: "text",
                }}
              >
                <RequiredRule />
              </SimpleItem>
              <SimpleItem
                dataField="description"
                editorType="dxTextArea"
                colSpan={2}
                editorOptions={{ height: 220 }}
              >
                <RequiredRule message="Descrierea în limba română este obligatorie" />
              </SimpleItem>
            </Form>
          </Editing>
          <FilterRow visible={true} />
          <Paging enabled={false} />
          <Scrolling mode="standard" showScrollbar="always" />
          <Column dataField="versionCode" caption="Versiune" width={120} />
          <Column
            dataField="description"
            caption="Descriere"
            cellRender={(cellInfo) => (
              <div style={{ whiteSpace: "pre-line" }}>{cellInfo.value}</div>
            )}
          />
          <Column
            dataField="statusCode"
            caption="Stare"
            width={100}
            allowEditing={false}
            calculateDisplayValue={(row: TWhatsNewEntry) =>
              whatsNewStatuses.find((item) => item.value === row.statusCode)
                ?.text || row.statusCode
            }
          />
          <Column
            dataField="sourceCode"
            caption="Sursă"
            width={170}
            calculateDisplayValue={(row: TWhatsNewEntry) =>
              whatsNewSources.find((item) => item.value === row.sourceCode)
                ?.text || row.sourceCode
            }
          />
          <Column
            dataField="updatedAt"
            caption="Actualizat"
            dataType="datetime"
            allowEditing={false}
            width={150}
          />
        </DataGrid>
      ) : (
        <DataGrid
          dataSource={roadmapDataSource}
          height={Math.max(420, drawerHeight - 170)}
          showBorders={true}
          showRowLines={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          wordWrapEnabled={true}
          onInitNewRow={(event) => {
            event.data.statusCode = "PLANNED";
            event.data.horizonCode = "NEAR_TERM";
          }}
        >
          <Editing
            mode="form"
            allowAdding={true}
            allowUpdating={true}
            allowDeleting={true}
            useIcons={true}
          >
            <Form labelMode="floating" labelLocation="top" colCount={2}>
              <SimpleItem dataField="title" colSpan={2}>
                <RequiredRule message="Titlul este obligatoriu" />
              </SimpleItem>
              <SimpleItem
                dataField="statusCode"
                editorType="dxSelectBox"
                editorOptions={{
                  items: roadmapStatuses,
                  valueExpr: "value",
                  displayExpr: "text",
                }}
              >
                <RequiredRule />
              </SimpleItem>
              <SimpleItem
                dataField="horizonCode"
                editorType="dxSelectBox"
                editorOptions={{
                  items: roadmapHorizons,
                  valueExpr: "value",
                  displayExpr: "text",
                }}
              >
                <RequiredRule />
              </SimpleItem>
              <SimpleItem dataField="targetVersion" />
              <SimpleItem
                dataField="description"
                editorType="dxTextArea"
                colSpan={2}
                editorOptions={{ height: 180 }}
              >
                <RequiredRule message="Descrierea este obligatorie" />
              </SimpleItem>
              <SimpleItem
                dataField="outcomeNote"
                editorType="dxTextArea"
                colSpan={2}
                editorOptions={{ height: 100 }}
              />
            </Form>
          </Editing>
          <FilterRow visible={true} />
          <Paging defaultPageSize={20} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[20, 50, 100]} />
          <Column dataField="title" caption="Plan" />
          <Column dataField="description" caption="Descriere" />
          <Column dataField="statusCode" caption="Stare" width={110} />
          <Column dataField="horizonCode" caption="Orizont" width={120} />
          <Column dataField="targetVersion" caption="Versiune țintă" width={120} />
          <Column
            dataField="updatedAt"
            caption="Actualizat"
            dataType="datetime"
            allowEditing={false}
            width={150}
          />
        </DataGrid>
      )}
    </div>
  );
};
